import * as React from "react";
import { cx } from "../internal/cx.js";
import { InfoFieldRow, type InfoFieldRowProps } from "../molecules/info-field-row.js";

/**
 * DetailsGrid — read-only details layout for drawers, profile panels,
 * confirmation steps, and summary sections.
 *
 * Composes InfoFieldRow instead of inventing a new row primitive. Domain
 * packages supply formatted values; the grid only owns grouping and
 * responsive layout.
 */

export type DetailsGridColumns = 1 | 2 | 3;
export type DetailsGridRow = Omit<InfoFieldRowProps, "layout"> & {
  id: string;
};

export interface DetailsGridProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "title"> {
  /** Row data. Use children instead when a row needs custom layout. */
  rows?: DetailsGridRow[];
  /** Custom row content. */
  children?: React.ReactNode;
  /** Responsive desktop column count. */
  columns?: DetailsGridColumns;
  /** Adds vertical dividers between columns on desktop. */
  divided?: boolean;
  /** Row layout. Default stacked for card/detail panes. */
  rowLayout?: InfoFieldRowProps["layout"];
  /** Optional heading shown above the grid. */
  title?: React.ReactNode;
  /** Optional supporting copy shown under the heading. */
  description?: React.ReactNode;
}

export const DetailsGrid = React.forwardRef<HTMLDivElement, DetailsGridProps>(function DetailsGrid(
  {
    children,
    className,
    columns = 2,
    description,
    divided = false,
    rowLayout = "stacked",
    rows,
    title,
    ...props
  },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cx(
        "cw-v2-details-grid",
        `cw-v2-details-grid--cols-${columns}`,
        divided ? "cw-v2-details-grid--divided" : null,
        className,
      )}
      {...props}
    >
      {title || description ? (
        <header className="cw-v2-details-grid__header">
          {title ? <div className="cw-v2-details-grid__title">{title}</div> : null}
          {description ? (
            <div className="cw-v2-details-grid__description">{description}</div>
          ) : null}
        </header>
      ) : null}
      <div className="cw-v2-details-grid__body">
        {rows?.map(({ id, ...row }) => (
          <InfoFieldRow key={id} layout={rowLayout} {...row} />
        ))}
        {children}
      </div>
    </div>
  );
});
DetailsGrid.displayName = "DetailsGrid";
